import React, { useEffect, useState } from "react";
import { Printer } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { MPDistrictMap } from "@/components/gov/MPDistrictMap";

function Kpi({ label, value }) {
  return (
    <div className="border border-[#E5DFD3] bg-[#FBF8F1] p-3">
      <div className="text-[9px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-bold text-slate-900 brand-heading">{value}</div>
    </div>
  );
}

export default function GovSummaryPrint() {
  const { t, tv } = useLang();
  const [s, setS] = useState(null);
  const [rows, setRows] = useState([]);
  const [sectors, setSectors] = useState([]);
  useEffect(() => {
    api.get("/reports/government-summary").then((r) => setS(r.data)).catch(() => {});
    api.get("/reports/district-stats").then((r) => setRows(r.data)).catch(() => {});
    api.get("/reports/sector-demand").then((r) => setSectors(r.data)).catch(() => {});
  }, []);
  if (!s) return <div className="p-10 text-slate-500">{t("loading")}</div>;
  const pct = (a, b) => (b ? Math.round((100 * a) / b) : 0);
  const top = [...rows].filter((r) => r.msmes).sort((a, b) => b.demand - a.demand).slice(0, 8);
  const sec = [...sectors].sort((a, b) => (b.total_amount || 0) - (a.total_amount || 0)).slice(0, 6);
  const secTotal = sec.reduce((x, y) => x + (y.total_amount || 0), 0) || 1;
  const untouched = rows.filter((r) => !r.msmes).length;
  const today = new Date().toLocaleDateString(localStorage.getItem("lang") === "hi" ? "hi-IN" : "en-IN", { day: "2-digit", month: "short", year: "numeric" });

  return (
    <div className="bg-slate-100 min-h-screen py-8 print:py-0 print:bg-white brand-body">
      <div className="no-print max-w-[210mm] mx-auto mb-4 flex justify-end">
        <button onClick={() => window.print()} data-testid="print-summary-btn" className="inline-flex items-center gap-2 rounded-md bg-slate-900 text-white px-4 py-2 text-sm font-semibold"><Printer className="w-4 h-4" /> {t("gs_print_now")}</button>
      </div>
      <article className="a4-sheet mx-auto bg-white shadow-xl print:shadow-none p-[12mm] text-slate-900" data-testid="print-summary">
        <header className="flex items-end justify-between border-b-2 border-slate-900 pb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#E86A2E]">{t("app_name")}</div>
            <h1 className="text-2xl font-bold brand-heading">{t("gs_title")}</h1>
          </div>
          <div className="text-right text-[10px] text-slate-500">{t("gs_state")}<br />{today}<br />{t("gs_nonpii")}</div>
        </header>
        <section className="mt-4 grid grid-cols-4 gap-0">
          <Kpi label={t("g_msmes")} value={s.registered_msmes} />
          <Kpi label={t("g_ready")} value={`${s.credit_ready_msmes} (${pct(s.credit_ready_msmes, s.registered_msmes)}%)`} />
          <Kpi label={t("g_demand")} value={formatINR(s.total_credit_demand)} />
          <Kpi label={t("g_apps")} value={s.total_applications} />
          <Kpi label={t("g_sanct")} value={`${s.total_sanctioned} (${pct(s.total_sanctioned, s.total_applications)}%)`} />
          <Kpi label={t("g_disb")} value={s.total_disbursed} />
          <Kpi label={t("g_women")} value={s.women_owned_cases} />
          <Kpi label={t("g_districts")} value={`${s.districts_covered} / 55`} />
        </section>
        <section className="mt-4 grid grid-cols-5 gap-5">
          <div className="col-span-3">
            <h2 className="text-xs font-bold uppercase tracking-widest mb-1">{t("gs_map")}</h2>
            <MPDistrictMap rows={rows} metric="demand" selected={null} onSelect={() => {}} />
          </div>
          <div className="col-span-2 text-[11px] leading-relaxed">
            <h2 className="text-xs font-bold uppercase tracking-widest mb-2">{t("gs_obs")}</h2>
            <ul className="space-y-1.5 list-disc pl-4">
              <li>{pct(s.credit_ready_msmes, s.registered_msmes)}% {t("gs_obs1")}</li>
              <li>{s.total_sanctioned} / {s.total_applications} {t("gs_obs2")}</li>
              <li>{s.women_owned_cases} {t("gs_obs3")}</li>
              <li>{untouched} / 55 {t("gs_obs4")}</li>
            </ul>
          </div>
        </section>
        <section className="mt-4 grid grid-cols-2 gap-5 text-[10.5px]">
          <div>
            <h2 className="text-xs font-bold uppercase tracking-widest mb-1">{t("gs_top")}</h2>
            <table className="w-full"><thead><tr className="bg-slate-900 text-white text-left"><th className="px-2 py-1">{t("col_district")}</th><th className="px-2 py-1 text-right">{t("g_msmes")}</th><th className="px-2 py-1 text-right">{t("g_demand")}</th><th className="px-2 py-1 text-right">{t("g_sanct")}</th></tr></thead>
              <tbody>{top.map((r, i) => <tr key={r.district} className={i % 2 ? "bg-[#F7F2E7]" : ""}><td className="px-2 py-0.5">{r.district}</td><td className="px-2 py-0.5 text-right">{r.msmes}</td><td className="px-2 py-0.5 text-right">{formatINR(r.demand)}</td><td className="px-2 py-0.5 text-right">{r.sanctioned}</td></tr>)}</tbody></table>
          </div>
          <div>
            <h2 className="text-xs font-bold uppercase tracking-widest mb-1">{t("gs_sector")}</h2>
            <table className="w-full"><thead><tr className="bg-slate-900 text-white text-left"><th className="px-2 py-1">{t("col_sector")}</th><th className="px-2 py-1 text-right">{t("g_demand")}</th><th className="px-2 py-1 text-right">%</th></tr></thead>
              <tbody>{sec.map((x, i) => <tr key={x.sector} className={i % 2 ? "bg-[#F7F2E7]" : ""}><td className="px-2 py-0.5">{tv(x.sector)}</td><td className="px-2 py-0.5 text-right">{formatINR(x.total_amount)}</td><td className="px-2 py-0.5 text-right">{pct(x.total_amount, secTotal)}%</td></tr>)}</tbody></table>
          </div>
        </section>
        <footer className="mt-5 border-t border-slate-200 pt-2 text-[8.5px] text-slate-500 leading-snug">{t("footer_legal")}</footer>
      </article>
    </div>
  );
}
